import { Component, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import '@gig/magic-ui-kit';
import '@gig/magic-pages';
import '@gig/magic-responsible-gaming-prompters';
import { magic } from '@gig/magic-sdk';
import type { IMessageEmit } from '@gig/magic-messaging';
import { notificationService } from '@gig/magic-messaging';
import type { IDepositDataEvent } from '@gig/magic-sdk/dist/events';
import { SessionCode } from '@gig/magic-sdk/dist/events';
import {
    CssDevice,
    FeatureEnabledV2,
    getFeatureEnabled,
    getFeatureValueV2,
    getTrackingService,
    ipCountryQuery,
    languageQuery,
    MagicConfigMixin,
    modalService,
    nativeAppService,
    queryTranslations,
    SessionChange,
} from '@gig/magic-common';
import '@gig/magic-gamification/dist/components/loyalty-progress';

import { MainNavigation } from '../main-navigation';
import { TopNavigation } from '../top-navigation';
import { BottomNavigation } from '../bottom-navigation';
import { AppFooter } from '../footer';
import { LiveChat } from '../live-chat';
import { ModalContainer } from '../modal-container';
import { RegilyManager } from '../regily-manager';
import { TermsUpdatedManager } from '../terms-updated-manager';
import { SourceOfWealthManager } from '../source-of-wealth-manager';
import { GameFrame } from '../game-frame';
import { ContinuePlaying } from '../continue-playing';
import { BlockManager } from '../block-manager';
import { ZendeskLoader } from '../zendesk-loader';
import { NotFound } from '../not-found';
import { SavageWidget } from '../savage-widget';

import type { OmitFirst } from '../../types';
import {
    AuthRestriction,
    config,
    intercom,
    liveChat,
    setHtmlLang,
    smarticoService,
    zendeskChat,
} from '../../services';
import {
    getTimeOutHandling,
    RETURNING_USER_KEY,
    TimeOutHandling,
    isModalOpen,
} from '../../util';

import { RouteName } from '../../routes/routes.constants';
import { prerender } from '../../services/seo/prerender.service';
import { syncOpenGraphMetaTags } from '../../services/seo/open-graph.service';
import {
    actions as rgActions,
    RESPONSIBLE_GAMING_NAMESPACE,
} from '../../store/modules/responsible-gaming';
import { responsibleGamingModalService } from '../responsible-gaming-modal/responsible-gaming-modal-service';

import '../../styles/default.scss';
import { JurisdictionMixin } from '../../mixins/jurisdiction.mixin';
import { NotFoundMixin } from '../../mixins/notfound.mixin';
import {
    ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
    BREAKPOINT_DEVICE_UPDATED,
    BREAKPOINT_FULLSCREEN_UPDATED,
    DEVICE_QUERY_IS_IOS,
    DEVICE_QUERY_IS_IPAD,
    DEVICE_QUERY_IS_MOBILE,
    ENABLE_CONTINUE_PLAYING_UPDATED,
    Feature,
    FeatureSettings,
    GAMES_CURRENT_GAME_SLUG_UPDATED,
    GAMES_JACKPOTS_LIST,
    GAMES_LIST,
    MESSAGING_MARK_READ,
    NET_POSITION_BAR_UPDATED,
    PANIC_BAR_VISIBLE,
    RECONNECTING_UPDATED,
    SINGLE_LANGUAGE_QUERY,
    USER_FIRST_LOG_IN_UPDATED,
} from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { alternateLink } from '@gig/magic-frontend-toolkit/dist/services/alternate-link';
import { linktag } from '@gig/magic-frontend-toolkit/dist/services/linktag';

const rgModule = namespace(RESPONSIBLE_GAMING_NAMESPACE);

// Safety valve for redirectAfterFirstLogin's retry loop, in case the
// configured URL can never actually be reached - not meant to bound normal
// sequential compliance flows (subdivision info, verification, etc.), which
// is why it's generous.
const MAX_FIRST_LOGIN_REDIRECT_ATTEMPTS = 20;
let firstLoginRedirectAttempts = 0;

const ZENDESK_ROUTES = [
    RouteName.FAQ,
    RouteName.FAQ_CATEGORIES,
    RouteName.FAQ_ENTRIES,
    RouteName.FAQ_SEARCH,
];

@Component({
    components: {
        MainNavigation,
        TopNavigation,
        BottomNavigation,
        AppFooter,
        LiveChat,
        ModalContainer,
        TermsUpdatedManager,
        SourceOfWealthManager,
        GameFrame,
        ContinuePlaying,
        BlockManager,
        RegilyManager,
        ZendeskLoader,
        NotFound,
        SavageWidget,
    },
})
export default class App extends Mixins(
    MagicConfigMixin,
    JurisdictionMixin,
    NotFoundMixin,
) {
    @FeatureEnabledV2({
        slug: Feature.COOKIE_POPUP,
    })
    cookiePopupEnabled = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;
    topNavigationSettings: string = '';

    country: Country | null = null;

    isLoggedIn = false;

    reconnecting: boolean = false;

    @rgModule.Action('clearLimits')
    clearLimits!: OmitFirst<typeof rgActions.clearLimits>;

    @rgModule.Action('loadUserBlocks')
    loadUserBlocks!: OmitFirst<typeof rgActions.loadUserBlocks>;

    @rgModule.Action('clearTimeoutState')
    clearTimeoutState!: OmitFirst<typeof rgActions.clearTimeoutState>;

    showNetPositionBar = false;
    enableContinuePlaying = false;
    isMobile = false;
    isFullscreen: boolean = false;
    shouldShowPWAInstall: boolean = false;
    currentGameSlug: string | null = null;
    panicBarVisible = false;
    isWalletOpen: boolean = false;

    private subscriptions: Array<() => void> = [];
    private firstLoginRedirectPending = false;
    private waitingForNavigationBeforeFirstLoginRedirect = false;

    get isTopNavWithBottomEnabled() {
        return (
            this.isTopNavigationEnabled &&
            this.topNavigationSettings.includes(
                FeatureSettings.BOTTOM_NAVIGATION_ENABLED,
            )
        );
    }

    get isMobileGamePlayView() {
        return !!this.currentGameSlug && this.isMobile;
    }

    get shouldDisableRGAlerts() {
        return this.$route.matched.some((route) => {
            return route.meta.shouldDisableRGAlerts === true;
        });
    }

    get shouldHideMainMenu() {
        const isLandingPage = this.$route.name === RouteName.LANDING;

        const hasHideMainMenuMeta = this.$route.matched.some((route) => {
            return route.meta.shouldHideMainMenu === true;
        });

        return hasHideMainMenuMeta || (!this.isMobile && isLandingPage);
    }

    get isNotFoundRoute(): boolean {
        return this.isNotFound;
    }

    get isSportsbookPage(): boolean {
        return this.$route.name === RouteName.SPORTSBOOK;
    }

    mounted() {
        const attributes = config.cookieBot;
        if (!attributes?.src) {
            return;
        }

        const cookieBot = document.createElement('script');
        for (const [key, value] of Object.entries(attributes)) {
            cookieBot.setAttribute(key, value);
        }
        document.head.appendChild(cookieBot);
    }

    async created() {
        this.country = await ipCountryQuery();

        if (!window.inTestEnvironment) {
            prerender.waitFor(this.requestGames(), this.requestJackpots());
        }

        this.setDefaultMetaTags();
        linktag.setLanguagesConfig(config.languages);

        this.topNavigationSettings = await getFeatureValueV2(
            Feature.TOP_NAVIGATION,
            '',
        );

        if (magic.session.isCreated()) {
            this.handleSessionCreated();
        }
        magic.events.sessionDestroyed(() => this.handleSessionDestroyed());
        magic.events.sessionCreated(() => this.handleSessionCreated());
        magic.events.sessionChange(this.handleSessionChanged);
        magic.events.firstDeposit(this.handleFirstDeposit);
        magic.events.subsequentDeposit(this.handleSubsequentDeposit);

        liveChat?.updateLiveChat(magic.session.getUser());
        intercom?.load();

        const language = await languageQuery();

        smarticoService.setUserData(magic.session.getUser(), language);

        this.checkIsWalletOpen();

        zendeskChat?.preloadZendesk?.();

        this.getPWAstatus();

        window.eventBus.subscribe<boolean>(
            RECONNECTING_UPDATED,
            (e) => {
                this.reconnecting = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            NET_POSITION_BAR_UPDATED,
            (event) => {
                this.showNetPositionBar = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            ENABLE_CONTINUE_PLAYING_UPDATED,
            (event) => {
                this.enableContinuePlaying = event.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<CssDevice>(
            BREAKPOINT_DEVICE_UPDATED,
            async () => {
                this.isMobile = await window.queryBus.query(
                    DEVICE_QUERY_IS_MOBILE,
                )!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            BREAKPOINT_FULLSCREEN_UPDATED,
            (e) => {
                this.isFullscreen = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<string | null>(
            GAMES_CURRENT_GAME_SLUG_UPDATED,
            (e) => {
                this.currentGameSlug = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(
            PANIC_BAR_VISIBLE,
            async (e) => {
                this.panicBarVisible = e.payload!;
            },
            { replay: true },
        );

        window.eventBus.subscribe<boolean>(USER_FIRST_LOG_IN_UPDATED, (e) => {
            if (!e.payload) {
                return;
            }

            this.firstLoginRedirectPending = true;
            this.waitForNavigationThenTryFirstLoginRedirect();
        });

        // Blocking modals (subdivision info, mandatory messages, identity
        // verification, etc.) don't always trigger a router navigation when
        // they close (some are injected straight into the DOM), so watch
        // modalService directly to retry once nothing is open anymore.
        modalService.onModalsChange((modals) => {
            if (modals.length === 0) {
                this.waitForNavigationThenTryFirstLoginRedirect();
            }
        });

        // A blocking modal can close while the user is still pending
        // verification (eg. failed a scan and was sent to upload documents
        // manually) - retry once that requirement actually clears.
        window.eventBus.subscribe<boolean>(
            ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
            (e) => {
                if (!e.payload) {
                    this.waitForNavigationThenTryFirstLoginRedirect();
                }
            },
        );
    }

    @Watch('magicConfig.language')
    async languageChange() {
        setHtmlLang(this.magicConfig.language);
        syncOpenGraphMetaTags();

        if (config?.zendesk?.version === 'legacy') {
            await zendeskChat?.setZendeskSettings?.(this.magicConfig.language);
        }
    }

    @Watch('$route.name', { immediate: true })
    async onRouteNameChanged(newRouteName: string) {
        if (config?.zendesk) {
            await zendeskChat?.preloadZendesk();

            if (
                newRouteName &&
                ZENDESK_ROUTES.includes(newRouteName as RouteName)
            ) {
                window.zE?.(() => {
                    zendeskChat?.showZendesk?.();
                    zendeskChat?.setZendeskSettings?.(
                        this.magicConfig.language,
                    );
                });
            } else {
                window.zE?.(() => {
                    zendeskChat?.hideZendesk?.();
                });
            }
        }

        const user = magic.session.getUser();
        const newLanguage = await languageQuery();
        smarticoService.setUserData(user, newLanguage);
    }

    private requestJackpots(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_JACKPOTS_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    @Watch('$route.path')
    onRouteChange() {
        this.setDefaultMetaTags();
        this.resetNotFound();
    }

    get isTopNavigationLight() {
        return this.$route.matched.reduce(
            (prev, cur) => ({ ...prev, ...cur.meta.topNavigation }),
            {
                isLight: false,
            },
        ).isLight;
    }

    private setDefaultMetaTags() {
        queryTranslations().then(() => {
            linktag.setCanonicalTagFromPath(this.$route.path);
            alternateLink.setAlternateTag(this.$route.path, {
                useBaseUrl: true,
            });
            syncOpenGraphMetaTags();
        });
    }

    @Watch('$route.query')
    checkIsWalletOpen() {
        const queryModalPath = this.$route.query.modal;
        this.isWalletOpen =
            (Array.isArray(queryModalPath) &&
                queryModalPath.some((path) =>
                    path?.includes(RouteName.WALLET),
                )) ||
            !!queryModalPath?.includes(RouteName.WALLET);
        this.resetNotFound();
    }

    @SessionChange({ immediate: true })
    async onSessionChange(isUserLoggedIn: boolean) {
        this.isLoggedIn = isUserLoggedIn;
    }

    onAcceptedCookiePopup() {
        getTrackingService().trackCookieAcceptance();
    }

    markMessageAsRead(payload: IMessageEmit) {
        window.commandBus.publish(MESSAGING_MARK_READ, payload);
    }

    async getPWAstatus() {
        const [isIOS, isIpad] = await Promise.all([
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IOS),
            window.queryBus.query<boolean>(DEVICE_QUERY_IS_IPAD),
        ]);
        this.shouldShowPWAInstall = isIOS! || isIpad!;
    }

    private requestGames(): Promise<void> {
        return new Promise((resolve) => {
            const subscription = window.eventBus.subscribe(
                GAMES_LIST,
                () => {
                    resolve();
                    subscription.unsubscribe();
                },
                { replay: true },
            );
        });
    }

    private async handleSessionDestroyed() {
        liveChat?.updateLiveChat(null);
        intercom?.shutdown();
        this.clearLimits();
        responsibleGamingModalService.close();

        await this.clearTimeoutState();

        smarticoService.setUserData(null, null);

        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];

        this.checkRouteRestriction();
    }

    private handleSessionChanged(code: SessionCode) {
        if (
            [
                SessionCode.EXPIRED,
                SessionCode.TERMINATED_BY_ANOTHER_LOGIN,
                SessionCode.TERMINATED_BY_PRESET_TIME,
                SessionCode.TERMINATED_BY_SELF_EXCLUSION,
                SessionCode.TERMINATED_BY_GEO_LOCATION,
            ].includes(code)
        ) {
            nativeAppService.postSystemLogout();
        }

        if (code === SessionCode.TERMINATED_BY_GEO_LOCATION) {
            notificationService.error(this.$t('session.logout.geo-location'));
        }

        if (SessionCode.LOGGED_OFF) {
            nativeAppService.postLogout();
        }
    }

    private handleFirstDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackFirstDeposit(amount, currency);
    }

    private handleSubsequentDeposit(event: IDepositDataEvent) {
        const { amount, currency } = event;
        nativeAppService.trackDeposit(amount, currency);
    }

    // Triggered right when the user's first login is confirmed - fires the
    // redirect immediately (after letting the triggering navigation settle,
    // to avoid racing with it) rather than waiting for other modals or
    // pending verification to clear. Those open on top of whatever page we
    // land on instead of blocking us from navigating there in the first
    // place. modalService/ACCOUNT_REQUIRES_VERIFICATION_UPDATED still retry
    // this as a fallback in case some other guard intercepted our push and
    // redirected elsewhere before we could arrive.
    private async tryFirstLoginRedirect() {
        if (!this.firstLoginRedirectPending) {
            return;
        }

        const enabled = await getFeatureEnabled(
            Feature.REDIRECTION_AFTER_FIRST_LOGIN,
        );
        if (!enabled) {
            this.firstLoginRedirectPending = false;
            return;
        }

        const configuredPath = await getFeatureValueV2<string>(
            Feature.REDIRECTION_AFTER_FIRST_LOGIN,
            '',
        );
        if (!configuredPath) {
            this.firstLoginRedirectPending = false;
            return;
        }

        const singleLanguage = await window.queryBus.query<boolean>(
            SINGLE_LANGUAGE_QUERY,
        );
        const redirectPath = singleLanguage
            ? configuredPath
            : `/${await languageQuery()}${configuredPath}`;

        if (this.hasArrivedAtFirstLoginRedirect(redirectPath)) {
            this.firstLoginRedirectPending = false;
            return;
        }

        firstLoginRedirectAttempts += 1;
        if (firstLoginRedirectAttempts > MAX_FIRST_LOGIN_REDIRECT_ATTEMPTS) {
            this.firstLoginRedirectPending = false;
            return;
        }

        this.$router.push({ path: redirectPath }).catch(() => {
            // Something else superseded our push (eg. a navigation that was
            // already in flight) - wait for the dust to settle and retry.
            this.waitForNavigationThenTryFirstLoginRedirect();
        });
    }

    // Ensures we only ever check/act once the router isn't itself mid-
    // transition, no matter which signal (login event, modal closing,
    // verification clearing, a superseded push) asked us to try. Checking
    // `router.currentRoute` while another navigation is still resolving
    // would race with it - this waits for whatever's in flight to confirm
    // first. Debounced so multiple signals firing close together don't
    // register duplicate one-off hooks.
    private waitForNavigationThenTryFirstLoginRedirect() {
        if (this.waitingForNavigationBeforeFirstLoginRedirect) {
            return;
        }

        this.waitingForNavigationBeforeFirstLoginRedirect = true;
        const unregister = this.$router.afterEach(() => {
            this.waitingForNavigationBeforeFirstLoginRedirect = false;
            unregister();
            this.tryFirstLoginRedirect();
        });
    }

    private hasArrivedAtFirstLoginRedirect(redirectPath: string): boolean {
        const target = this.$router.resolve(redirectPath).route;
        const current = this.$router.currentRoute;

        if (!target.name) {
            return current.fullPath === redirectPath;
        }

        return (
            current.name === target.name ||
            isModalOpen(target.name as RouteName, current.query, this.$router)
        );
    }

    private checkRouteRestriction() {
        const lastRestriction = this.$route.matched.reduce(
            (prev, { meta }) =>
                meta.authRestriction !== undefined
                    ? meta.authRestriction
                    : prev,
            AuthRestriction.UNRESTRICTED,
        );
        if (lastRestriction === AuthRestriction.AUTH_REQUIRED) {
            if (this.isAGCO || this.isAGLC || this.isLOTBA) {
                // lotba, agco and aglc display the session resume modal when user logs out,
                // on standard flow the user is redirected to login screen which covers the
                // session modal. We ensure therefore the user is redirected to a non auth route (HOME)
                // so the login modal is not displayed and the session resume modal can be displayed
                this.$router.push({
                    name: RouteName.HOME,
                });
            } else {
                this.$router.replace({
                    name: RouteName.LOGIN,
                    query: {
                        backTo: this.$route.fullPath,
                    },
                });
            }
        }
    }

    /**
     * Loads the Time-out state of a restored session and terminates it in the
     * jurisdictions where being logged in during a Time-out is not permitted.
     */
    private async resolveTimeOutBlock() {
        const timeOutHandling = await getTimeOutHandling();

        if (timeOutHandling === TimeOutHandling.NONE) {
            return;
        }

        const isTimedOut = await this.loadUserBlocks();

        if (!isTimedOut || timeOutHandling !== TimeOutHandling.LOGIN_BLOCKED) {
            return;
        }

        notificationService.error(this.$t('login.login-failed.blocked'), {
            uniqueID: 'time-out-login-blocked',
        });

        try {
            await magic.methods.users.logout();
        } catch (error) {
            console.error('Error logging out a timed out user:', error);
        }
    }

    private async handleSessionCreated() {
        const user = magic.session.getUser();
        const language = await languageQuery();

        liveChat?.updateLiveChat(user);
        intercom?.update(user);
        smarticoService.setUserData(magic.session.getUser(), language);

        if (user) {
            await this.resolveTimeOutBlock();
        }

        this.subscriptions.push(
            magic.events.sowqRequested(() => {
                if (this.$route.name !== RouteName.SOW) {
                    this.$router.push({
                        name: RouteName.SOW,
                    });
                }
            }).unsub,
        );

        try {
            window.localStorage.setItem(RETURNING_USER_KEY, '1');
        } catch (e) {
            console.error(e);
        }
    }
}
